import {
  loadManifest,
  setManifest,
} from '@angular-architects/module-federation';
import { environment } from './environments/environment';

type LoadManifestFn = (manifestUrl: string) => Promise<unknown>;
type BootstrapLoaderFn = () => Promise<unknown>;
type LoggerFn = (message?: unknown, ...optionalParams: unknown[]) => void;
type ManifestEntry =
  | string
  | {
      remoteEntry?: string;
      entry?: string;
      [key: string]: unknown;
    };
type ManifestFile = Record<string, ManifestEntry>;

interface StartAppOptions {
  hostname?: string;
  modulesConfigFileUrl?: string;
  loadManifestFn?: LoadManifestFn;
  bootstrapLoader?: BootstrapLoaderFn;
  logger?: LoggerFn;
}

declare global {
  var __MF_HOST_DISABLE_AUTO_START__: boolean | undefined;
}

export const getManifestFromHostname = (hostname: string): string => {
  if (hostname.includes('-qa.')) {
    return '/assets/modules-config-qa.json';
  }

  if (hostname.includes('-dev.')) {
    return '/assets/modules-config-dev.json';
  }

  if (hostname.includes('localhost')) {
    return '/assets/modules-config-local.json';
  }

  return '/assets/modules-config.json';
};

export const buildManifestCandidates = (
  hostname: string,
  modulesConfigFileUrl: string,
): string[] => {
  const fromHostname = getManifestFromHostname(hostname);
  const prefixedManifest = fromHostname.startsWith('/assets/')
    ? `/mf-host_Front${fromHostname}`
    : fromHostname;

  return [...new Set([fromHostname, prefixedManifest, modulesConfigFileUrl])];
};

const isLocalHost = (hostname: string): boolean =>
  !hostname || hostname.includes('localhost');

export const appendCacheBust = (url: string, cacheBust: string): string => {
  const parsedUrl = new URL(
    url,
    globalThis.location?.origin ?? 'http://localhost',
  );
  parsedUrl.searchParams.set('v', cacheBust);

  if (url.startsWith('http://') || url.startsWith('https://')) {
    return parsedUrl.toString();
  }

  return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
};

export const buildCacheBustedManifest = (
  manifest: ManifestFile,
  cacheBust: string,
): ManifestFile => {
  const result: ManifestFile = {};

  for (const [name, entry] of Object.entries(manifest)) {
    if (typeof entry === 'string') {
      result[name] = appendCacheBust(entry, cacheBust);
      continue;
    }

    const remoteEntry =
      typeof entry.remoteEntry === 'string'
        ? appendCacheBust(entry.remoteEntry, cacheBust)
        : undefined;
    const moduleEntry =
      typeof entry.entry === 'string'
        ? appendCacheBust(entry.entry, cacheBust)
        : undefined;

    result[name] = {
      ...entry,
      ...(remoteEntry ? { remoteEntry } : {}),
      ...(moduleEntry ? { entry: moduleEntry } : {}),
    };
  }

  return result;
};

export const loadManifestWithCacheBusting = async (
  manifestUrl: string,
  hostname: string,
): Promise<void> => {
  if (isLocalHost(hostname)) {
    await loadManifest(manifestUrl);
    return;
  }

  const cacheBust = `${Date.now()}`;
  const manifestResponse = await fetch(
    appendCacheBust(manifestUrl, cacheBust),
    {
      cache: 'no-store',
    },
  );

  if (!manifestResponse.ok) {
    throw new Error(
      `No se pudo descargar el manifiesto ${manifestUrl}: ${manifestResponse.status}`,
    );
  }

  const manifest = (await manifestResponse.json()) as ManifestFile;
  const cacheBustedManifest = buildCacheBustedManifest(manifest, cacheBust);

  await setManifest(cacheBustedManifest as never);
};

export const loadManifestWithFallback = async (
  options: Pick<
    StartAppOptions,
    'hostname' | 'modulesConfigFileUrl' | 'loadManifestFn' | 'logger'
  > = {},
): Promise<void> => {
  const hostname = options.hostname ?? globalThis.location?.hostname ?? '';
  const modulesConfigFileUrl =
    options.modulesConfigFileUrl ?? environment.modulesConfigFileUrl;
  const loadManifestFn =
    options.loadManifestFn ??
    ((manifestUrl: string) =>
      loadManifestWithCacheBusting(manifestUrl, hostname));
  const logger = options.logger ?? console.error;
  const manifestCandidates = buildManifestCandidates(
    hostname,
    modulesConfigFileUrl,
  );

  let lastError: unknown;

  for (const manifestUrl of manifestCandidates) {
    try {
      await loadManifestFn(manifestUrl);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  logger('No se pudo cargar ningún archivo de configuración de módulos.', {
    candidates: manifestCandidates,
    error: lastError,
  });
};

export const startApp = (options: StartAppOptions = {}): Promise<unknown> => {
  const bootstrapLoader =
    options.bootstrapLoader ?? (() => import('./bootstrap'));

  return loadFlagsBeforeStart().then(() =>
    loadManifestWithFallback(options).finally(bootstrapLoader),
  );
};

const loadFlagsBeforeStart = async (): Promise<void> => {
  try {
    // Importar dinámicamente para evitar circular dependency
    const { MicrofrontendsFlagsService } =
      await import('./app/core/services/microfrontends-flags.service');
    const service = new MicrofrontendsFlagsService();
    await service.loadFlags();
  } catch (error) {
    console.warn(
      'No se pudieron cargar las banderas de microfrontends:',
      error,
    );
  }
};

if (!globalThis.__MF_HOST_DISABLE_AUTO_START__) {
  startApp();
}
